/** Idioma de traducción y región de formato se resuelven por separado. */
/** Clock input stays canonical HH:mm; display follows the session language. */
export function parseTimeInput(value, locale) {
    const english = /^en(?:-|$)/i.test(String(locale).replace(/_/g, '-'))
    const match = (english ? /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i : /^(\d{1,2}):(\d{2})$/).exec(String(value || '').trim())
    if (!match) return null
    let hour = Number(match[1])
    const minute = Number(match[2])
    if (minute > 59 || hour > (english ? 12 : 23) || (english && hour < 1)) return null
    if (english) hour = hour % 12 + (match[3].toUpperCase() === 'PM' ? 12 : 0)
    return String(hour).padStart(2, '0') + ':' + match[2]
}

export function formatTimeInput(value, locale, chart = false) {
    const match = /^(\d{1,2})(?::(\d{2})(?::\d{2})?)?h?$/.exec(String(value == null ? '' : value))
    if (!match || Number(match[1]) > 23 || Number(match[2] || 0) > 59) return ''
    const hour = Number(match[1])
    const minute = match[2] || '00'
    if (/^en(?:-|$)/i.test(String(locale).replace(/_/g, '-'))) {
        return (hour % 12 || 12) + (chart && minute === '00' ? '' : ':' + minute) + (hour < 12 ? ' AM' : ' PM')
    }
    return String(hour).padStart(2, '0') + ':' + minute
}

/** Decimal schedule hours can carry a day offset; never mutate that source value. */
export function formatHourTime(value, locale, chart = false) {
    if (value == null || value === '' || !Number.isFinite(Number(value))) return ''
    const minutes = ((Math.round(Number(value) * 60) % 1440) + 1440) % 1440
    return formatTimeInput(Math.floor(minutes / 60) + ':' + String(minutes % 60).padStart(2, '0'), locale, chart)
}

/** Elapsed time does not wrap at 24 hours. Round once before splitting units. */
export function formatDuration(value, { unit = 'minutes', emptyValue = '-' } = {}) {
    if (value == null || value === '' || !Number.isFinite(Number(value))) return emptyValue
    const factors = { minutes: 1, hours: 60, days: 1440 }
    if (!Object.prototype.hasOwnProperty.call(factors, unit)) return emptyValue
    const total = Math.round(Math.abs(Number(value)) * factors[unit])
    const hours = Math.floor(total / 60)
    const minutes = total % 60
    return (Number(value) < 0 && total ? '-' : '') + (hours ? hours + 'h' + (minutes ? ' ' : '') : '') + (minutes || !hours ? minutes + 'min' : '')
}

export function resolveDateInputLocale(language, ...regionalLocales) {
    const normalized = String(language || 'es').replace(/_/g, '-')
    const base = normalized.split('-')[0].toLowerCase()
    const region = regionalLocales.filter(value => typeof value === 'string').map(value => value.replace(/_/g, '-')).find(value => value.includes('-') && value.split('-')[0].toLowerCase() === base)
    const candidate = normalized.includes('-') ? normalized : (region || normalized)
    try {
        return new Intl.DateTimeFormat(candidate).resolvedOptions().locale
    } catch (_) {
        return 'es-ES'
    }
}

function datePattern(locale) {
    return new Intl.DateTimeFormat(locale, {
        year: 'numeric', month: '2-digit', day: '2-digit', numberingSystem: 'latn'
    }).formatToParts(new Date(2026, 3, 13))
}

/** English usa inglés estadounidense, también en sesiones británicas heredadas. */
export function resolveDateDisplayLocale(language, config = {}, ...regionalLocales) {
    const normalized = String(language || 'es').replace(/_/g, '-')
    const locale = resolveDateInputLocale(normalized, ...regionalLocales)
    if (/^en(?:-|$)/i.test(locale)) {
        return 'en-US'
    }
    return locale
}

export function dateDisplayPatterns(locale, dateFormat = 'EU') {
    const resolved = resolveDateDisplayLocale(locale, { dateFormat })
    const standard = /^(de|en|es|fr|it|pt)(-|$)/i.test(resolved)
    const legacyUs = String(dateFormat).toUpperCase() === 'US' || String(dateFormat).includes('MM/DD')
    const english = standard ? /^en(-|$)/i.test(resolved) : legacyUs
    const us = standard ? /^en-US$/i.test(resolved) : legacyUs
    const tokens = { day: 'DD', month: 'MM', year: 'YYYY' }
    const date = standard ? datePattern(resolved).map(part => tokens[part.type] || part.value).join('') : (us ? 'MM/DD/YYYY' : 'DD/MM/YYYY')
    const time = english ? 'h:mm A' : 'HH:mm'
    return { locale: resolved, date, time, datetime: date + ' ' + time, chart: date.replace(/^YYYY[./-]|[./-]YYYY$/, '') }
}

/** Puntuación del estándar explícita para evitar cambios entre versiones de ICU. */
export function formatCalendarDate(date, locale, style) {
    const formatter = new Intl.DateTimeFormat(locale, {
        ...(style === 'monthYear' ? { month: 'short', year: 'numeric' } : { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }),
        timeZone: 'UTC'
    })
    const parts = {}
    formatter.formatToParts(date).forEach(part => { parts[part.type] = part.value })
    if (style === 'monthYear') {
        if (/^pt(?:-PT)?$/i.test(locale)) return String(date.getUTCMonth() + 1).padStart(2, '0') + '/' + parts.year
        const month = /^(de|fr)(-|$)|^pt-BR$/i.test(locale) ? parts.month : parts.month.replace(/\.$/, '')
        return month + ' ' + parts.year
    }
    if (/^en-US$/i.test(locale)) return parts.weekday + ', ' + parts.month + ' ' + parts.day + ', ' + parts.year
    if (/^en-GB$/i.test(locale)) return parts.weekday + ', ' + parts.day + ' ' + parts.month + ' ' + parts.year
    return formatter.format(date)
}

export function dateInputPlaceholder(locale, mode = 'date') {
    const year = /^(es|pt|fr|it)(-|$)/i.test(locale) ? 'aaaa' : 'yyyy'
    const day = /^fr(-|$)/i.test(locale) ? 'jj' : /^it(-|$)/i.test(locale) ? 'gg' : 'dd'
    const tokens = { day, month: 'mm', year }
    const date = datePattern(locale).map(part => tokens[part.type] || part.value).join('')
    return date + (mode === 'datetime' ? (/^en(-|$)/i.test(locale) ? ' hh:mm AM/PM' : ' hh:mm') : '')
}

export function formatDateInput(value, locale, mode = 'date') {
    if (!value || typeof value !== 'string') return ''
    const match = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::\d{2})?)?$/.exec(value)
    if (!match) return ''
    const values = { year: match[1], month: match[2], day: match[3] }
    const date = datePattern(locale).map(part => values[part.type] || part.value).join('')
    if (mode !== 'datetime') return date
    const hour = Number(match[4] || 0)
    const time = /^en(-|$)/i.test(locale)
        ? (hour % 12 || 12) + ':' + (match[5] || '00') + (hour >= 12 ? ' PM' : ' AM')
        : (match[4] || '00') + ':' + (match[5] || '00')
    return date + ' ' + time
}

/** Devuelve ISO local; nunca adivina un segundo orden de día y mes. */
export function parseDateInput(value, locale, mode = 'date') {
    if (typeof value !== 'string' || !value.trim()) return null
    const fields = []
    const pattern = datePattern(locale).map(part => {
        if (['year', 'month', 'day'].includes(part.type)) {
            fields.push(part.type)
            return part.type === 'year' ? '(\\d{4})' : '(\\d{1,2})'
        }
        return part.value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    }).join('')
    const twelveHour = /^en(-|$)/i.test(locale)
    const suffix = mode === 'datetime' ? '\\s+(\\d{1,2}):(\\d{2})' + (twelveHour ? '\\s+(AM|PM)' : '') : ''
    const match = new RegExp('^' + pattern + suffix + '$', 'i').exec(value.trim())
    if (!match) return null
    const date = {}
    fields.forEach((field, index) => { date[field] = Number(match[index + 1]) })
    const parsed = new Date(0)
    parsed.setFullYear(date.year, date.month - 1, date.day)
    parsed.setHours(0, 0, 0, 0)
    if (date.year < 1 || parsed.getFullYear() !== date.year || parsed.getMonth() !== date.month - 1 || parsed.getDate() !== date.day) return null
    const pad = number => String(number).padStart(2, '0')
    const iso = String(date.year).padStart(4, '0') + '-' + pad(date.month) + '-' + pad(date.day)
    if (mode !== 'datetime') return iso
    let hour = Number(match[fields.length + 1])
    const minute = Number(match[fields.length + 2])
    if (twelveHour) {
        if (hour < 1 || hour > 12) return null
        hour = hour % 12 + (match[fields.length + 3].toUpperCase() === 'PM' ? 12 : 0)
    }
    return hour < 24 && minute < 60 ? iso + 'T' + pad(hour) + ':' + pad(minute) : null
}

export function isDateInputInRange(value, min, max) {
    min = typeof min === 'string' && /^\d{4}-\d{2}-\d{2}/.test(min) ? min.replace(' ', 'T') : ''
    max = typeof max === 'string' && /^\d{4}-\d{2}-\d{2}/.test(max) ? max.replace(' ', 'T') : ''
    const compare = bound => value.slice(0, String(bound).length > 10 ? 16 : 10)
    return (!min || compare(min) >= min) && (!max || compare(max) <= max)
}

/** vue-functional-calendar recibe los nombres desde lunes y rota domingo internamente. */
export function dateInputCalendar(locale) {
    // The shared product standard pins pt-PT to Monday, independently of ICU.
    let sundayStart = /^(en(-US)?|pt(-BR|-PT)?)$/i.test(locale)
    if (typeof Intl.Locale === 'function') {
        const region = new Intl.Locale(locale)
        const week = typeof region.getWeekInfo === 'function' ? region.getWeekInfo() : region.weekInfo
        if (week) sundayStart = week.firstDay === 7
    }
    if (/^pt(?:-PT)?$/i.test(locale)) sundayStart = false
    const dayNames = /^es(-|$)/i.test(locale)
        ? ['L', 'M', 'X', 'J', 'V', 'S', 'D']
        : Array.from({ length: 7 }, (_, index) => {
            const name = new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(new Date(2026, 3, 13 + index)).replace(/\.$/, '')
            return /^en(-|$)/i.test(locale) ? name.slice(0, 2) : name
        })
    return {
        dayNames,
        dayNamesFull: Array.from({ length: 7 }, (_, index) => new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(new Date(2026, 3, 13 + index))),
        monthNames: Array.from({ length: 12 }, (_, month) => new Intl.DateTimeFormat(locale, { month: 'long' }).format(new Date(2026, month, 1))),
        sundayStart
    }
}
